import React, { useEffect, useState } from 'react';
import {
  Typography, TableContainer, Paper, Table, TableHead,
  TableRow, TableCell, TableBody, IconButton, Tooltip, Checkbox,
  Box
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditAssignmentDialog from './EditAssignmentDialog';
import EditMassAssignmentDialog from './EditMassAssignmentDialog';
import { Assignment, useAssignmentService } from '../services/assignmentService';

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "An unknown error occurred";
}

const CurrentAssignmentsTab: React.FC<{ assessmentId: number }> = ({ assessmentId }) => {
  const assignmentService = useAssignmentService();
  const [currentAssignments, setCurrentAssignments] = useState<Assignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null);
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignmentIds, setAssignmentIds] = useState<number[]>([]);
  const [editMassDialogOpen, setEditMassDialogOpen] = useState(false);

  useEffect(() => {
    setAssignmentsLoading(true);
    setAssignmentsError(null);
    assignmentService.getAssignmentsForScenario(assessmentId)
      .then(setCurrentAssignments)
      .catch(err => setAssignmentsError(getErrorMessage(err)))
      .finally(() => setAssignmentsLoading(false));
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  // (Optional) Debug: see selected IDs
  useEffect(() => {
    console.log(assignmentIds);
  }, [assignmentIds]);

  const handleDelete = async (assignmentId: number) => {
    if (window.confirm('Are you sure you want to remove this assignment?')) {
      setAssignmentsLoading(true);
      try {
        await assignmentService.deleteAssignment(assignmentId);
        setCurrentAssignments(a => a.filter(assn => assn.id !== assignmentId));
      } catch (err) {
        setAssignmentsError(getErrorMessage(err));
      } finally {
        setAssignmentsLoading(false);
      }
    }
  };

  const handleEdit = (assignment: Assignment) => {
    setEditingAssignment(assignment);
    setEditDialogOpen(true);
  };

  const handleEditSave = async (updatedFields: Partial<Assignment>) => {
    if (!editingAssignment) return;
    setAssignmentsLoading(true);
    try {
      const updated = await assignmentService.updateAssignment(editingAssignment.id, updatedFields);
      setCurrentAssignments(a =>
        a.map(assn => (assn.id === updated.id ? updated : assn))
      );
      setEditDialogOpen(false);
      setEditingAssignment(null);
    } catch (err) {
      setAssignmentsError(getErrorMessage(err));
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const handleMassEditSave = async (assignmentIds: number[], updatedFields: Partial<Assignment>) => {
    setAssignmentsLoading(true);
    try {
      await Promise.all(
        assignmentIds.map(id =>
          assignmentService.updateAssignment(id, updatedFields)
        )
      );
      // Optionally refresh assignment list
      const updated = await assignmentService.getAssignmentsForScenario(assessmentId);
      setCurrentAssignments(updated);
      setEditMassDialogOpen(false);
      setAssignmentIds([]);
      console.log('Updating assignments:', assignmentIds, updatedFields);
    } catch (err) {
      setAssignmentsError(getErrorMessage(err));
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const handleMassEdit = () => {
    if (assignmentIds.length === 0) {
      alert('Please select at least one assignment to edit.');
      return;
    }
    setEditMassDialogOpen(true);
  };

  const handleMultiSelect = (assignmentId: number) => {
    setAssignmentIds(prev =>
      prev.includes(assignmentId)
        ? prev.filter(num => num !== assignmentId)
        : [...prev, assignmentId]
    );
  };

  if (assignmentsLoading) return <Typography>Loading...</Typography>;
  if (assignmentsError) return <Typography color="error">{assignmentsError}</Typography>;

  return currentAssignments.length > 0 ? (
    <Box sx={{height:'70vh'}}>
      <TableContainer component={Paper} sx={{ height:'100%', overflow:'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <Tooltip title="Edit selected assignments">
                  <IconButton onClick={handleMassEdit} size="small">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell>User Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned At</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentAssignments.map(a => (
              <TableRow key={a.id}>
                <TableCell>
                  <Checkbox 
                    checked={assignmentIds.includes(a.id)}
                    size="small"
                    onChange={() => handleMultiSelect(a.id)}
                  />
                </TableCell>
                <TableCell>{a.user_email}</TableCell>
                <TableCell>{a.status || 'assigned'}</TableCell>
                <TableCell>
                  {a.assigned_at ? new Date(a.assigned_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell>
                  {a.start_date ? new Date(a.start_date).toLocaleString() : '—'}
                </TableCell>
                <TableCell>
                  {a.end_date ? new Date(a.end_date).toLocaleString() : '—'}
                </TableCell>
                <TableCell align="left">
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEdit(a)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton onClick={() => handleDelete(a.id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <EditAssignmentDialog
        open={editDialogOpen}
        assignment={editingAssignment}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleEditSave}
      />
      <EditMassAssignmentDialog
        open={editMassDialogOpen}
        assignmentIds={assignmentIds}
        onClose={() => setEditMassDialogOpen(false)}
        onSave={handleMassEditSave}
      />
    </Box>
  ) : (
    <Typography>No assignments for this scenario.</Typography>
  );
};

export default CurrentAssignmentsTab;